import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {AppConfigService} from './app.config.service';
import {CookieService} from 'ngx-cookie-service';
import {UserVO} from "../generate/UserVO";
import {BehaviorSubject, Observable} from "rxjs";
import {LoginAuthResponseVO} from "../model/loginAuthResponseVO";
import {LoginResponseVO} from "../model/loginResponseVo";
import {LoginRecoveryResponseVO} from "../model/loginRecoveryResponseVO";

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    currentUserSubject: BehaviorSubject<UserVO>;

    constructor(
        private httpClient: HttpClient,
        private router: Router,
        private configService: AppConfigService,
        @Inject(PLATFORM_ID) private platformId: Object,
        private cookieService: CookieService) {
      this.currentUserSubject = new BehaviorSubject<UserVO>(null);
    }

    public createHeadersOptions() {
        const options = {'Accept': 'application/json'};
        options['X-RZMS-XSRF-TOKEN'] = this.cookieService.get('RZMS-TOKEN');
        return {headers: new HttpHeaders(options), withCredentials: true};
    }

    logoutClientSide() {
        if (this.cookieService.check('RZMS-TOKEN')) {
            this.cookieService.delete('RZMS-TOKEN');
        }
        this.clearLocalStorage();
    }

    logout(message?: string, success?: boolean) {
        this.httpClient.delete(this.getLogoutRestEndPoint(), this.createHeadersOptions()).subscribe(data => {
        }, error => {
            this.clearLocalStorage();
            this.redirectToHome(message || 'Logged out', success || false);
        }, () => {
            this.clearLocalStorage();
            this.redirectToHome(message || 'Logged out', success || false);
        });
    }

    error() {
        this.router.navigate(['/error']);
    }

    isLoggedIn(): boolean {
        if (isPlatformBrowser(this.platformId)) {
            return this.currentUserAuthVO() != null;
        }
        return false;
    }


    loginAndStoreToken(loginName: string, password: string, buildInfo?: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.httpClient.post(this.getLoginRestEndPoint(), {loginName, password, buildInfo}, {withCredentials: true}).subscribe(data => {
                this.storeUserDetails(data);
                resolve(data);
            }, error => {
                console.log(error);
                reject(error);
            }, () => {
            });
        });
    }

    login(payload: any): Observable<LoginAuthResponseVO|LoginResponseVO> {
        return this.httpClient.post<LoginAuthResponseVO|LoginResponseVO>(this.getLoginRestEndPoint(), payload, {withCredentials: true});
    }

    loginRecovery(recoveryCode: string, token: string): Observable<LoginRecoveryResponseVO> {
        const payload = {
            mfaCode: recoveryCode,
            mfaCodeType: "recovery",
            token: token
        };
        return this.httpClient.post<LoginRecoveryResponseVO>(
            this.getLoginRestEndPoint(),
            payload,
            {withCredentials: true}
        );
    }

    validateTokenAndStoreAttributes(token: string): Promise<Object> {
        return new Promise((resolve, reject) => {
            this.httpClient.post(this.getTokenValidationEndpoint(), token, {withCredentials: true}).subscribe(data => {
                resolve(data);
            }, error => {
                console.log(error);
                reject(error);
            }, () => {
            });
        });
    }

    validateResetPasswordToken(token: string, userId: string, mfaCode): Observable<any> {
        return this.httpClient.post(`${this.configService.config.staffBackendUrl}` + '/validateResetPasswordToken', {
            userId: userId,
            sessionJWT: token,
            mfaCode: mfaCode,
            mfaCodeType: mfaCode && mfaCode.length === 6 ? 'TOTP' : 'RECOVERY'
        }, {withCredentials: true});
    }

    currentLoginName() {
        return this.currentUserAuthVO()['loginName'];
    }

    currentUserID() {
        return this.currentUserAuthVO()['id'];
    }

    currentUserFullName() {
        return this.currentUserAuthVO()['firstName'] + ' ' + this.currentUserAuthVO()['lastName'];
    }

    currentUserEmail() {
        return this.currentUserAuthVO()['email'];
    }

    currentUserAuthVO(): UserVO {
        if (!this.getIfPlatformBrowser() || !localStorage.getItem('user')) {
            return null;
        }
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            this.currentUserSubject.next(user);
            return user;
        } catch (e) {
            return null;
        }
    }

    storeUserAuthVO(user: UserVO) {
        localStorage.setItem('user', JSON.stringify(user));
        this.currentUserSubject.next(user);
    }

    lastLoginInfo() {
        if (localStorage.getItem('lastLogin')) {
            return JSON.parse(localStorage.getItem('lastLogin'));
        }
    }

    storeUserDetails(loginResponse: any) {
        if (this.getIfPlatformBrowser()) {
            this.clearLocalStorage();
            localStorage.setItem('user', JSON.stringify(loginResponse.user));
            localStorage.setItem('lastLogin', JSON.stringify(loginResponse.lastLoginInfo));
        }
    }

    clearLocalStorage() {
        localStorage.removeItem('user');
        localStorage.removeItem('lastLogin');
    }

    getLoginRestEndPoint(): string {
        return `${this.configService.config.staffBackendUrl}` + '/login';
    }

    getTokenValidationEndpoint(): string {
        return `${this.configService.config.staffBackendUrl}` + '/validateToken';
    }

    getLogoutRestEndPoint(): string {
        if (this.configService.config) {
            return `${this.configService.config.staffBackendUrl}` + '/logout';
        } else {
            return '';
        }
    }

    public redirectToHome(errorMessage?: string, success?: boolean) {
        this.router.navigate(['/home'], {
            state: {
                error: errorMessage,
                success: success
            }
        });
    }

    getIfPlatformBrowser() {
        return isPlatformBrowser(this.platformId);
    }
}

